import React, { useState, useEffect } from 'react';

import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import FeedbackModal from '../components/landing/FeedbackModal';
import { User, FileText, Download, MessageSquare, Quote } from 'lucide-react';
import api from '../utils/axios';
import getFullUrl from '../utils/formatUrl';

const StudentDesk = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [content, setContent] = useState({});
    const [approvedPosts, setApprovedPosts] = useState([]);
    const [colleges, setColleges] = useState([]);
    
    // Feedback form states
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [formData, setFormData] = useState({
        name: '', studentCode: '', address: '', course: '', college: '', photo: '',
        duration: '', year: '', place: '', company: '', feedback: ''
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        fetchApprovedPosts();
        fetchColleges();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    const fetchApprovedPosts = async () => {
        try {
            const res = await api.get('/student-posts/approved');
            setApprovedPosts(res.data.data);
        } catch (err) {
            console.error("Failed to load approved posts", err);
        }
    };

    const fetchColleges = async () => {
        try {
            const res = await api.get('/colleges/active');
            setColleges(res.data.data || []);
        } catch (err) {
            console.error("Failed to load colleges", err);
        }
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmitFeedback = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/student-posts/submit', formData);
            setSubmitSuccess(true);
            setFormData({
                name: '', studentCode: '', address: '', course: '', college: '', photo: '',
                duration: '', year: '', place: '', company: '', feedback: ''
            });
            // Re-fetch posts just in case, though they won't appear until admin approval
            fetchApprovedPosts();
        } catch (err) {
            alert("Failed to submit. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Parse PDFs
    const studentDeskPdfs = (() => {
        try {
            return content.student_desk_pdfs ? JSON.parse(content.student_desk_pdfs) : [];
        } catch(e) {
            return [];
        }
    })();


    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
            />

            {/* Hero Section */}
            <header className="relative h-[300px] md:h-[450px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-[#003366]/90 z-10"></div>
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full text-center">
                    <div className="bg-amber-400 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl">
                        <User size={32} className="text-[#003366]" />
                    </div>
                    <h1 className="text-4xl md:text-7xl font-black text-white uppercase tracking-tighter mb-4">
                        {content.student_desk_title || 'Student Desk'}
                    </h1>
                    <p className="text-slate-400 font-bold uppercase tracking-[4px] md:tracking-[8px] text-[10px] md:text-xs">
                        {content.student_desk_desc || 'Empowering Digital Learning'}
                    </p>
                </div>
            </header>




            {/* Student Feedback Entry Section */}
            <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none">
                    <Quote size={400} className="text-white" />
                </div>
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-12 text-center md:text-left">
                        <div className="max-w-2xl px-4 md:px-0">
                            <h3 className="text-3xl md:text-5xl font-black uppercase tracking-tighter mb-4 leading-none italic">Share Your <span className="text-[#cc0000]">Excellence</span> Story</h3>
                            <p className="text-slate-400 font-bold uppercase text-[10px] md:text-xs tracking-widest leading-relaxed">Have you achieved success after your course? Or have some quality feedback for us? Your fellow students would love to hear from you!</p>
                        </div>
                        <button 
                            onClick={() => setIsFeedbackOpen(true)}
                            className="bg-[#cc0000] w-[90%] md:w-auto justify-center text-white px-6 py-4 md:px-12 md:py-6 font-black uppercase text-[10px] md:text-xs tracking-[3px] md:tracking-[5px] hover:bg-white hover:text-slate-900 transition-all shadow-[0_0_50px_rgba(204,0,0,0.3)] flex items-center group shrink-0"
                        >
                            <MessageSquare className="w-5 h-5 mr-3 group-hover:scale-125 transition-transform" />
                            Give Feedback
                        </button>
                    </div>
                </div>
            </section>

            {/* Student Wall / Approved Feedbacks */}
            {approvedPosts.length > 0 && (
                <section className="py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-16">
                            <h3 className="text-3xl font-black uppercase tracking-tighter mb-4">Student <span className="text-[#cc0000]">Voice Wall</span></h3>
                            <div className="w-24 h-1 bg-[#cc0000] mx-auto mt-6"></div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {approvedPosts.map((post, idx) => (
                                <div key={idx} className="bg-white border border-slate-100 p-8 rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-500 relative group overflow-hidden">
                                     <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                        <Quote size={60} className="text-slate-900" />
                                    </div>
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="w-14 h-14 rounded-full bg-slate-900 overflow-hidden border-2 border-slate-50 flex items-center justify-center text-white font-black text-xs shrink-0">
                                            {post.photo ? (
                                                <img src={getFullUrl(post.photo)} alt={post.name} className="w-full h-full object-cover" />
                                            ) : (
                                                post.name.charAt(0)
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="font-extrabold text-[#003366] text-base uppercase tracking-tight mb-1 truncate">{post.name}</h4>
                                            <div className="text-[7px] font-black text-[#cc0000] uppercase tracking-widest leading-tight">
                                                {post.course} • {post.college}<br/>
                                                Batch {post.year}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-slate-600 font-medium text-sm leading-relaxed italic mb-8 border-l-2 border-slate-100 pl-4 py-1">
                                        "{post.feedback}"
                                    </p>
                                    <div className="pt-6 border-t border-slate-50 flex flex-wrap gap-4">
                                        <div className="flex-1">
                                            <span className="block text-[7px] font-black uppercase text-slate-400 mb-1">Placed At</span>
                                            <span className="block text-[10px] font-black text-slate-900 uppercase">{post.company}</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="block text-[7px] font-black uppercase text-slate-400 mb-1">Location</span>
                                            <span className="block text-[10px] font-black text-slate-900 uppercase">{post.place}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Downloads & Circulars Section */}
            {studentDeskPdfs.length > 0 && (
                <section className="py-24 bg-slate-50 border-t border-slate-200">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-16">
                            <h3 className="text-3xl font-black uppercase tracking-tighter mb-4">Downloads & <span className="text-[#cc0000]">Circulars</span></h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {studentDeskPdfs.map((pdf, idx) => (
                                <a
                                    key={idx}
                                    href={getFullUrl(pdf.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group bg-white border border-slate-200 rounded-sm p-6 hover:shadow-xl hover:border-[#cc0000]/30 transition-all duration-300 flex items-start space-x-5"
                                >
                                    <div className="w-14 h-14 bg-[#cc0000]/10 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-[#cc0000] transition-colors duration-300">
                                        <FileText className="w-6 h-6 text-[#cc0000] group-hover:text-white transition-colors duration-300" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-bold text-slate-900 text-sm mb-1 group-hover:text-[#cc0000] transition-colors truncate">
                                            {pdf.title || 'Untitled Document'}
                                        </h4>
                                        <div className="flex items-center text-[10px] font-black uppercase tracking-widest text-[#003366] group-hover:text-[#cc0000] transition-colors">
                                            <Download size={12} className="mr-2" />
                                            Download
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <Footer data={content} />

            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
            
            <FeedbackModal 
                isOpen={isFeedbackOpen} 
                onClose={() => {
                    setIsFeedbackOpen(false);
                    setSubmitSuccess(false);
                }}
                onSubmit={handleSubmitFeedback}
                formData={formData}
                handleFormChange={handleFormChange}
                isSubmitting={isSubmitting}
                submitSuccess={submitSuccess}
                colleges={colleges}
            />
        </div>
    );
};

export default StudentDesk;

